import { ExpenseFormValues } from '../types/expense';

export interface AmountCalculationResult {
  gross: number;
  gstAmount: number;
  gstPercent: number;
  total: number;
}

export class AmountCalculator {
  /**
   * Computes totals for expense amounts including GST calculations
   */
  static computeTotals(values: Partial<ExpenseFormValues>): AmountCalculationResult {
    const mode = values.amountMode || values.mode || 'total';
    const enableGst = values.enableGst ?? true;
    const gstAmount = parseFloat(values.gstAmount?.toString() || '0') || 0;

    let gross = 0;
    let gstPercent = 0;

    if (mode === 'unit') {
      // Unit mode: quantity × rate = gross
      const quantity = parseFloat(values.quantity?.toString() || '0') || 0;
      const rate = parseFloat(values.rate?.toString() || '0') || 0;
      gross = quantity * rate;
    } else {
      // Total mode: use provided gross or amount
      gross = parseFloat(values.gross?.toString() || values.amount?.toString() || '0') || 0;
    }

    // Calculate GST percentage: gstPercent = (gstAmount / gross) * 100
    if (gross > 0 && gstAmount > 0) {
      gstPercent = (gstAmount / gross) * 100;
    }

    const total = gross + (enableGst ? gstAmount : 0);

    return {
      gross: parseFloat(gross.toFixed(2)),
      gstAmount: parseFloat(gstAmount.toFixed(2)),
      gstPercent: parseFloat(gstPercent.toFixed(2)),
      total: parseFloat(total.toFixed(2))
    };
  }

  /**
   * Validates amount calculations
   */
  static validateAmounts(values: Partial<ExpenseFormValues>): string[] {
    const errors: string[] = [];
    const mode = values.amountMode || values.mode || 'total';
    const enableGst = values.enableGst ?? true;

    if (mode === 'unit') {
      const quantity = parseFloat(values.quantity?.toString() || '0') || 0;
      const rate = parseFloat(values.rate?.toString() || '0') || 0;

      if (quantity <= 0) {
        errors.push('Quantity must be greater than 0');
      }
      if (rate <= 0) {
        errors.push('Rate must be greater than 0');
      }
    }

    if (enableGst) {
      const gstAmount = parseFloat(values.gstAmount?.toString() || '0') || 0;
      if (gstAmount < 0) {
        errors.push('GST amount cannot be negative');
      }
    }

    return errors;
  }

  /**
   * Formats amount for display
   */
  static formatAmount(amount: number): string {
    return `₹${amount.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  /**
   * Formats GST percentage for display
   */
  static formatGstPercent(percent: number): string {
    return `${percent.toFixed(2)}%`;
  }
}
